import { type ReactNode } from 'react';
import {
  type ComplianceItem,
  type FooterItemType,
  footerItemType,
  type FooterLevelTwoItem,
} from '~/vendor/cx-web/domain-types';
import classnames from 'classnames';

import { type SocialLink } from '../../types';
import AppStoreLinks from '../AppStoreLinks/index';
import FooterLogos from '../FooterLogos/index';

import styles from './FooterMeta.module.scss';

const SocialLinkAnchor = ({ SvgIcon, href, name }: { SvgIcon: SocialLink['SvgIcon']; href: string; name: string }) => {
  return (
    <a
      className={styles.footerSocialsLink}
      data-testid="social-link"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      title={name}
    >
      <SvgIcon aria-label={`${name} icon`} className={styles.footerSocialsImage} />
    </a>
  );
};

const ComplianceLogo = ({ ctaUri, imageUri }: { ctaUri: string; imageUri: string }) => {
  return (
    <a
      className={styles.complianceLogoLink}
      data-testid="compliance-logo-link"
      href={ctaUri}
      rel="noopener noreferrer"
      target="_blank"
      title="Compliance Logo"
    >
      {/* NGF-6364: lazy-load this below-fold footer image so it doesn't compete with the LCP fetch.
          width/height are intentionally omitted — imageUri is a CMS-driven URL with no known
          dimensions ("where controllable"). Shared component, so RR7 atlas benefits too. */}
      <img alt="Compliance Logo" loading="lazy" src={imageUri} />
    </a>
  );
};

type FooterMetaProps = {
  isUkFooterStyle?: boolean;
  legalAge: string;
  legalText: string;
  levelTwo: FooterLevelTwoItem[];
  logo: ReactNode;
  logoType: FooterItemType;
  socialLinks: SocialLink[];
};

export const FooterMeta = ({
  isUkFooterStyle,
  legalAge,
  legalText,
  levelTwo,
  logo,
  logoType,
  socialLinks,
}: FooterMetaProps) => {
  const levelTwoData = levelTwo || [];

  const isComplianceItem = (item: FooterLevelTwoItem): item is ComplianceItem => 'imageUri' in item;
  const complianceLogos = levelTwoData.filter(isComplianceItem).filter(item => item.type === logoType);

  const hasAppStoreLinks = levelTwoData.some(item => item.type === footerItemType.APP);
  const hasDisclaimer = levelTwoData.some(item => item.type === footerItemType.DISCLAIMER);

  const availableSocialLinks = socialLinks.filter(({ href }) => !!href);

  return (
    <section aria-label="Footer Meta Section" className={styles.footerMetaWrapper} data-testid="footer-meta-section">
      <div className={classnames(styles.footerMetaContainer, isUkFooterStyle && styles.footerMetaContainerUk)}>
        <div className={classnames(styles.footerLogoContainer, isUkFooterStyle && styles.footerLogoContainerUk)}>
          {isUkFooterStyle ? (
            <FooterLogos logo={logo} />
          ) : (
            <a data-testid="logo-link" href="./" rel="noopener noreferrer" target="_blank" title="Oddschecker">
              {logo}
            </a>
          )}
        </div>
        {isUkFooterStyle && hasAppStoreLinks ? (
          <div className={styles.appStoreLinksContainer}>
            <AppStoreLinks />
          </div>
        ) : null}
        {complianceLogos.length ? (
          <div className={styles.complianceLogosContainer}>
            {complianceLogos.map(({ ctaUri, imageUri }) => (
              <ComplianceLogo ctaUri={ctaUri} imageUri={imageUri} key={imageUri} />
            ))}
          </div>
        ) : null}
        {!isUkFooterStyle || hasDisclaimer ? (
          <div className={styles.footerRegulationContainer} data-testid="disclaimer">
            <span className={styles.legalAge}>{legalAge}</span>
            {legalText}
          </div>
        ) : null}
        <div className={styles.footerSocialsContainer} data-testid="socials">
          {availableSocialLinks.map(({ SvgIcon, href, name }) => {
            return <SocialLinkAnchor SvgIcon={SvgIcon} href={href} key={name} name={name} />;
          })}
        </div>
      </div>
    </section>
  );
};
